import api from "./api";
import { errorHandling } from "./error.handling";
import { API_URLS } from "../Constants/api.constants";

const NC8_EQUIPMENT = API_URLS.EQUIPMENT.replace("/manufacturer", "");
const basePath = `${NC8_EQUIPMENT}/equipment-models`;
const commandsBasePath = `${NC8_EQUIPMENT}/equipment-model-commands`;
const equipmentCommandsBasePath = `${NC8_EQUIPMENT}/commands`;
const commandAccessLevelsBasePath = `${NC8_EQUIPMENT}/equipment-commands/access-levels`;

const buildEquipmentModelPayload = (payload = {}) => ({
  EquipmentModelName:
    payload?.EquipmentModelName ??
    payload?.equipmentModelName ??
    payload?.description ??
    payload?.name ??
    "",
  ManufacturerId:
    payload?.ManufacturerId ?? payload?.manufacturerId ?? payload?.manufacturer ?? null,
  Type: payload?.Type ?? payload?.type ?? "",
  IsVirtualIgnition:
    payload?.IsVirtualIgnition ??
    payload?.isVirtualIgnition ??
    payload?.HasVirtualIgnition ??
    "N",
  MovementIndev:
    payload?.MovementIndev ??
    payload?.movementIndev ??
    payload?.HasImproperMovementDetection ??
    "N",
  SleepMode: payload?.SleepMode ?? payload?.sleepMode ?? payload?.HasSleepMode ?? "N",
  PcNumber: payload?.PcNumber ?? payload?.pcNumber ?? payload?.PC ?? null,
  IsImplemented: payload?.IsImplemented ?? payload?.isImplemented ?? "N",
});

async function fetchEquipmentModels(filters = {}) {
  try {
    const queryPayload = {
      EquipmentModelId:
        filters?.EquipmentModelId ?? filters?.equipmentModelId ?? filters?.id ?? "",
      Active: filters?.Active ?? filters?.active ?? "",
    };

    const query = new URLSearchParams(
      Object.entries(queryPayload).filter(
        ([, value]) => value !== "" && value !== null && value !== undefined
      )
    ).toString();

    const url = query ? `${basePath}?${query}` : basePath;
    const response = await api.get(url);

    const treated = errorHandling.handleResponse(
      response,
      "equipmentModelsService.fetchEquipmentModels"
    );

    if (treated) {
      return treated;
    }

    return null;
  } catch (error) {
    errorHandling.cathError(error, "equipmentModelsService.fetchEquipmentModels");
    return null;
  }
}

async function createEquipmentModel(payload = {}) {
  try {
    const requestPayload = buildEquipmentModelPayload(payload);

    const response = await api.post(basePath, JSON.stringify(requestPayload));

    const treated = errorHandling.handleResponse(
      response,
      "equipmentModelsService.createEquipmentModel"
    );

    if (treated) {
      return treated;
    }

    return null;
  } catch (error) {
    errorHandling.cathError(error, "equipmentModelsService.createEquipmentModel");
    return null;
  }
}

async function updateEquipmentModel(idOrPayload, payload = null) {
  try {
    const inputPayload = payload ?? idOrPayload ?? {};
    const equipmentModelId =
      payload === null
        ? idOrPayload?.EquipmentModelId ??
          idOrPayload?.equipmentModelId ??
          idOrPayload?.id
        : idOrPayload;

    if (equipmentModelId === undefined || equipmentModelId === null || equipmentModelId === "") {
      return null;
    }

    const requestPayload = buildEquipmentModelPayload(inputPayload);

    const response = await api.put(
      `${basePath}/${equipmentModelId}`,
      JSON.stringify(requestPayload)
    );

    const treated = errorHandling.handleResponse(
      response,
      "equipmentModelsService.updateEquipmentModel"
    );

    if (treated) {
      return treated;
    }

    return null;
  } catch (error) {
    errorHandling.cathError(error, "equipmentModelsService.updateEquipmentModel");
    return null;
  }
}

async function linkEquipmentModelCommand(equipmentModelId, commandId) {
  try {
    const response = await api.post(
      commandsBasePath,
      JSON.stringify({ EquipmentModelId: equipmentModelId, CommandId: commandId })
    );

    const treated = errorHandling.handleResponse(
      response,
      "equipmentModelsService.linkEquipmentModelCommand"
    );

    return treated ?? null;
  } catch (error) {
    errorHandling.cathError(error, "equipmentModelsService.linkEquipmentModelCommand");
    throw error;
  }
}

async function unlinkEquipmentModelCommand(equipmentModelId, commandId) {
  try {
    const response = await api.delete(
      commandsBasePath,
      { data: JSON.stringify({ EquipmentModelId: equipmentModelId, CommandId: commandId }) }
    );
    // API may return 204 for successful delete — treat it as success
    if (response && response.status === 204) {
      return { CommandId: commandId };
    }

    const treated = errorHandling.handleResponse(
      response,
      "equipmentModelsService.unlinkEquipmentModelCommand"
    );

    return treated ?? null;
  } catch (error) {
    errorHandling.cathError(error, "equipmentModelsService.unlinkEquipmentModelCommand");
    throw error;
  }
}

async function fetchEquipmentModelCommands(filters = {}) {
  try {
    const queryPayload = {
      EquipmentModelId: filters?.EquipmentModelId ?? filters?.equipmentModelId ?? "",
      CommandId: filters?.CommandId ?? filters?.commandId ?? "",
    };

    const query = new URLSearchParams(
      Object.entries(queryPayload).filter(
        ([, value]) => value !== "" && value !== null && value !== undefined
      )
    ).toString();

    const url = query ? `${commandsBasePath}?${query}` : commandsBasePath;

    const response = await api.get(url);

    const treated = errorHandling.handleResponse(
      response,
      "equipmentModelsService.fetchEquipmentModelCommands"
    );

    return treated ?? null;
  } catch (error) {
    errorHandling.cathError(error, "equipmentModelsService.fetchEquipmentModelCommands");
    throw error;
  }
}

async function fetchEquipmentCommands(filters = {}) {
  try {
    const queryPayload = {
      CommandId: filters?.CommandId ?? filters?.commandId ?? filters?.id ?? "",
      Active: filters?.Active ?? filters?.active ?? "",
    };

    const query = new URLSearchParams(
      Object.entries(queryPayload).filter(
        ([, value]) => value !== "" && value !== null && value !== undefined
      )
    ).toString();

    const url = query ? `${equipmentCommandsBasePath}?${query}` : equipmentCommandsBasePath;
    const response = await api.get(url);

    const treated = errorHandling.handleResponse(
      response,
      "equipmentModelsService.fetchEquipmentCommands"
    );

    return treated ?? null;
  } catch (error) {
    errorHandling.cathError(error, "equipmentModelsService.fetchEquipmentCommands");
    throw error;
  }
}

async function fetchEquipmentCommandAccessLevels(commandId) {
  try {
    const resolvedCommandId =
      commandId?.CommandId ?? commandId?.commandId ?? commandId?.id ?? commandId;

    if (resolvedCommandId === undefined || resolvedCommandId === null || resolvedCommandId === "") {
      return null;
    }

    const response = await api.get(
      `${NC8_EQUIPMENT}/equipment-commands/${resolvedCommandId}/access-levels`
    );

    const treated = errorHandling.handleResponse(
      response,
      "equipmentModelsService.fetchEquipmentCommandAccessLevels"
    );

    return treated ?? null;
  } catch (error) {
    errorHandling.cathError(
      error,
      "equipmentModelsService.fetchEquipmentCommandAccessLevels"
    );
    throw error;
  }
}

async function linkEquipmentCommandAccessLevel(commandId, accessLevelId) {
  try {
    const resolvedCommandId =
      commandId?.CommandId ?? commandId?.commandId ?? commandId?.id ?? commandId;
    const resolvedAccessLevelId =
      accessLevelId?.AccessLevelId ??
      accessLevelId?.accessLevelId ??
      accessLevelId?.id ??
      accessLevelId;

    const response = await api.post(
      commandAccessLevelsBasePath,
      JSON.stringify({ CommandId: resolvedCommandId, AccessLevelId: resolvedAccessLevelId })
    );

    const treated = errorHandling.handleResponse(
      response,
      "equipmentModelsService.linkEquipmentCommandAccessLevel"
    );

    return treated ?? null;
  } catch (error) {
    errorHandling.cathError(
      error,
      "equipmentModelsService.linkEquipmentCommandAccessLevel"
    );
    throw error;
  }
}

async function unlinkEquipmentCommandAccessLevel(commandId, accessLevelId) {
  try {
    const resolvedCommandId =
      commandId?.CommandId ?? commandId?.commandId ?? commandId?.id ?? commandId;
    const resolvedAccessLevelId =
      accessLevelId?.AccessLevelId ??
      accessLevelId?.accessLevelId ??
      accessLevelId?.id ??
      accessLevelId;

    const response = await api.delete(commandAccessLevelsBasePath, {
      data: JSON.stringify({ CommandId: resolvedCommandId, AccessLevelId: resolvedAccessLevelId }),
    });
      // treat 204 as successful unlink
      if (response && response.status === 204) {
        return { AccessLevelId: resolvedAccessLevelId };
      }

      const treated = errorHandling.handleResponse(
        response,
        "equipmentModelsService.unlinkEquipmentCommandAccessLevel"
      );

      return treated ?? null;
  } catch (error) {
    errorHandling.cathError(
      error,
      "equipmentModelsService.unlinkEquipmentCommandAccessLevel"
    );
    throw error;
  }
}

export const equipmentModelsService = {
  fetchEquipmentModels,
  createEquipmentModel,
  updateEquipmentModel,
  fetchEquipmentCommands,
  fetchEquipmentModelCommands,
  linkEquipmentModelCommand,
  unlinkEquipmentModelCommand,
  fetchEquipmentCommandAccessLevels,
  linkEquipmentCommandAccessLevel,
  unlinkEquipmentCommandAccessLevel,
};
